import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, Input, Select, ContentLoader } from '@/components/common';
import { AddressFields } from '@/components/forms/AddressFields';
import { useCustomerMe, useUpdateCustomerMe } from '@/hooks';
import { digitsOnlyOnChange, nameOnChange } from '@/utils/validation';
import { useAuthStore } from '@/store';
import { settingsService } from '@/services';

const profileSchema = z.object({
  salutation: z.string().optional(),
  first_name: z.string().min(1, 'First name is required'),
  middle_name: z.string().optional(),
  last_name: z.string().min(1, 'Last name is required'),
  date_of_birth: z.string().optional(),
  gender: z.enum(['male', 'female', 'other']).optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  alternate_phone: z.string().optional(),
  whatsapp_number: z.string().optional(),
  address_line1: z.string().optional(),
  address_line2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  pincode: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const salutationOptions = [
  { value: 'mr', label: 'Mr.' },
  { value: 'mrs', label: 'Mrs.' },
  { value: 'ms', label: 'Ms.' },
  { value: 'dr', label: 'Dr.' },
  { value: 'prof', label: 'Prof.' },
];

const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

/**
 * B2: `Customer.city` is an FK into the seeded `geography.City` table, so
 * `/customers/me/` answers it as a UUID and this page printed it -- read-only in
 * the Address block AND as the seed value of the editable City box, where typing
 * a real city over it then 400ed. `city_name` is the readable half, added to
 * `CustomerProfileSerializer` alongside a `validate_city` that resolves a name
 * or an id.
 *
 * The hook this page uses (`@/hooks` -> hooks/useCustomers) types its answer as
 * the shared `Customer`, which has no `city_name`, and both that hook and
 * `types/index.ts` are outside this task's file domain -- so the extra read
 * shape is narrowed here, the way this file already reaches for
 * `alternate_phone` and `whatsapp_number`.
 */
function cityNameOf(profile?: unknown): string | undefined {
  return (profile as { city_name?: string | null } | undefined)?.city_name || undefined;
}

function ReadOnlyField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-sm text-gray-900">{value || '—'}</p>
    </div>
  );
}

export function ProfilePage() {
  const [isEditing, setIsEditing] = useState(false);

  const { data: profile, isLoading } = useCustomerMe();
  const updateMutation = useUpdateCustomerMe();
  const authUser = useAuthStore((state) => state.user);
  const setAuthUser = useAuthStore((state) => state.setUser);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
  });

  useEffect(() => {
    if (profile) {
      reset({
        salutation: profile.salutation || undefined,
        first_name: profile.first_name,
        middle_name: profile.middle_name || undefined,
        last_name: profile.last_name,
        date_of_birth: profile.date_of_birth || undefined,
        gender: (profile.gender as ProfileFormData['gender']) || undefined,
        email: profile.email || '',
        phone: profile.phone || undefined,
        alternate_phone: (profile as unknown as Record<string, unknown>).alternate_phone as string | undefined,
        whatsapp_number: (profile as unknown as Record<string, unknown>).whatsapp_number as string | undefined,
        address_line1: profile.address_line1 || undefined,
        address_line2: profile.address_line2 || undefined,
        city: cityNameOf(profile),
        state: profile.state || undefined,
        pincode: profile.pincode || undefined,
      });
    }
  }, [profile, reset]);

  const onSubmit = (data: ProfileFormData) => {
    // #653: a blank date of birth is "none". Not sent when there was none on
    // file (so any other edit still saves); sent as blank to clear a stored one.
    const payload = { ...data, date_of_birth: data.date_of_birth || (profile?.date_of_birth ? '' : undefined) };
    updateMutation.mutate(payload, {
      onSuccess: () => {
        setIsEditing(false);
        // C1 follow-up: this screen edits the `customers.Customer` row (the
        // record broker/POSP screens read), but the logged-in identity
        // Header.tsx greets by -- name, initials, avatar -- comes from
        // `users.User` (`useAuthStore().user`), a separate row for the same
        // person. Before C1, a customer's only editor was Settings > Profile,
        // which wrote straight to `users.User` and kept the header current.
        // Mirror the overlapping fields there too so that edit path is not
        // lost. Best-effort: the customer record above already saved, so a
        // failure here (network blip) does not need to interrupt the user or
        // be surfaced as this screen's own error.
        settingsService
          .updateProfile({
            first_name: data.first_name,
            last_name: data.last_name,
            phone: data.phone || undefined,
          })
          .then((updatedUser) => {
            if (authUser) {
              setAuthUser({
                ...authUser,
                first_name: updatedUser.first_name,
                last_name: updatedUser.last_name,
                full_name: updatedUser.full_name,
                phone: updatedUser.phone,
              });
            }
          })
          .catch(() => {
            /* best-effort header sync only; see comment above */
          });
      },
    });
  };

  const handleCancel = () => {
    if (profile) {
      reset({
        salutation: profile.salutation || undefined,
        first_name: profile.first_name,
        middle_name: profile.middle_name || undefined,
        last_name: profile.last_name,
        date_of_birth: profile.date_of_birth || undefined,
        gender: (profile.gender as ProfileFormData['gender']) || undefined,
        email: profile.email || '',
        phone: profile.phone || undefined,
        alternate_phone: (profile as unknown as Record<string, unknown>).alternate_phone as string | undefined,
        whatsapp_number: (profile as unknown as Record<string, unknown>).whatsapp_number as string | undefined,
        address_line1: profile.address_line1 || undefined,
        address_line2: profile.address_line2 || undefined,
        city: cityNameOf(profile),
        state: profile.state || undefined,
        pincode: profile.pincode || undefined,
      });
    }
    setIsEditing(false);
  };

  if (isLoading) {
    return <ContentLoader />;
  }

  return (
    <div>
      <PageHeader
        title="My Profile"
        subtitle="View and manage your personal details"
        breadcrumbs={[
          { label: 'Settings', href: '/settings' },
          { label: 'Profile' },
        ]}
        actions={
          !isEditing ? (
            <Button
              variant="outline"
              leftIcon={<PencilIcon className="w-4 h-4" />}
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                leftIcon={<XMarkIcon className="w-4 h-4" />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                leftIcon={<CheckIcon className="w-4 h-4" />}
                isLoading={updateMutation.isPending}
                onClick={handleSubmit(onSubmit)}
              >
                Save
              </Button>
            </div>
          )
        }
      />

      <div className="space-y-6">
        {/* Personal Details */}
        <Card>
          <CardHeader
            title="Personal Details"
            subtitle={isEditing ? 'Update your personal information' : 'Your personal information'}
          />

          {isEditing ? (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <Select
                  label="Salutation"
                  options={salutationOptions}
                  placeholder="Select..."
                  {...register('salutation')}
                />
                <Input
                  label="First Name"
                  error={errors.first_name?.message}
                  required
                  {...register('first_name', { onChange: nameOnChange })}
                />
                <Input
                  label="Middle Name"
                  {...register('middle_name')}
                />
                <Input
                  label="Last Name"
                  error={errors.last_name?.message}
                  required
                  {...register('last_name', { onChange: nameOnChange })}
                />
                <Input
                  label="Date of Birth"
                  type="date"
                  error={errors.date_of_birth?.message}
                  {...register('date_of_birth')}
                />
                <Select
                  label="Gender"
                  options={genderOptions}
                  placeholder="Select..."
                  error={errors.gender?.message}
                  {...register('gender')}
                />
                <Input
                  label="Email Address"
                  type="email"
                  error={errors.email?.message}
                  {...register('email')}
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  error={errors.phone?.message}
                  {...register('phone', { onChange: digitsOnlyOnChange })}
                />
                <Input
                  label="Alternate Phone"
                  type="tel"
                  inputMode="numeric"
                  {...register('alternate_phone', { onChange: digitsOnlyOnChange })}
                />
                <Input
                  label="WhatsApp Number"
                  type="tel"
                  inputMode="numeric"
                  {...register('whatsapp_number', { onChange: digitsOnlyOnChange })}
                />
              </div>

              <div className="mt-8">
                <h4 className="text-sm font-medium text-gray-700 mb-4">Address</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Input
                    label="Address Line 1"
                    {...register('address_line1')}
                  />
                  <Input
                    label="Address Line 2"
                    {...register('address_line2')}
                  />
                  {/* B2: seeded from `city_name` and posted back by name.
                      `Customer.city` is an FK into the seeded geography.City
                      table, so this box used to be filled with the UUID -- and
                      typing a real city over it 400ed, because the profile
                      serializer only took an id. Both halves are fixed:
                      `CustomerProfileSerializer.validate_city` now resolves a
                      name or an id, and refuses a name the table does not have
                      rather than silently storing no city at all. */}
                  {/* B6: PIN pre-fills State (and City); State is always a
                      dropdown. */}
                  <AddressFields
                    pincode={watch('pincode') ?? ''}
                    onPincodeChange={(value) => setValue('pincode', value, { shouldValidate: true, shouldDirty: true })}
                    pincodeError={errors.pincode?.message}
                    city={watch('city') ?? ''}
                    onCityChange={(value) => setValue('city', value, { shouldValidate: true, shouldDirty: true })}
                    cityError={errors.city?.message}
                    state={watch('state') ?? ''}
                    onStateChange={(value) => setValue('state', value, { shouldValidate: true, shouldDirty: true })}
                    stateError={errors.state?.message}
                  />
                </div>
              </div>
            </form>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <ReadOnlyField label="Salutation" value={profile?.salutation} />
                <ReadOnlyField label="First Name" value={profile?.first_name} />
                <ReadOnlyField label="Middle Name" value={profile?.middle_name} />
                <ReadOnlyField label="Last Name" value={profile?.last_name} />
                <ReadOnlyField label="Date of Birth" value={profile?.date_of_birth} />
                <ReadOnlyField label="Gender" value={profile?.gender} />
                <ReadOnlyField label="Email" value={profile?.email} />
                <ReadOnlyField label="Phone" value={profile?.phone} />
                <ReadOnlyField
                  label="Alternate Phone"
                  value={(profile as unknown as Record<string, unknown>)?.alternate_phone as string | undefined}
                />
                <ReadOnlyField
                  label="WhatsApp Number"
                  value={(profile as unknown as Record<string, unknown>)?.whatsapp_number as string | undefined}
                />
              </div>

              {(profile?.address_line1 || cityNameOf(profile) || profile?.state || profile?.pincode) && (
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <h4 className="text-sm font-medium text-gray-700 mb-4">Address</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <ReadOnlyField label="Address Line 1" value={profile?.address_line1} />
                    <ReadOnlyField label="Address Line 2" value={profile?.address_line2} />
                    <ReadOnlyField label="City" value={cityNameOf(profile)} />
                    <ReadOnlyField label="State" value={profile?.state} />
                    <ReadOnlyField label="Pincode" value={profile?.pincode} />
                  </div>
                </div>
              )}
            </>
          )}
        </Card>

        {/* Account Details (read-only) */}
        <Card>
          <CardHeader
            title="Account Details"
            subtitle="Your account and broker information"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <ReadOnlyField
              label="Customer ID"
              value={(profile as unknown as Record<string, unknown>)?.customer_code as string | undefined}
            />
            <ReadOnlyField
              label="Member Since"
              value={(profile as unknown as Record<string, unknown>)?.customer_since as string | undefined}
            />
            <ReadOnlyField
              label="Broker Name"
              value={(profile as unknown as Record<string, unknown>)?.broker_name as string | undefined}
            />
            <ReadOnlyField
              label="Broker Phone"
              value={(profile as unknown as Record<string, unknown>)?.broker_phone as string | undefined}
            />
            <ReadOnlyField
              label="Broker Email"
              value={(profile as unknown as Record<string, unknown>)?.broker_email as string | undefined}
            />
            <ReadOnlyField
              label="POSP Name"
              value={(profile as unknown as Record<string, unknown>)?.posp_name as string | undefined}
            />
            <ReadOnlyField
              label="POSP Phone"
              value={(profile as unknown as Record<string, unknown>)?.posp_phone as string | undefined}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}
